/**
 * Portal-side mirror of backend/config/featureCatalog.js.
 *
 * Kept in lock-step with the backend on purpose, and deliberately NOT fetched:
 * nav has to render on the first paint, before any request resolves, and a
 * feature list that arrives late shows the user a menu that then rearranges
 * itself. The company's own array already rides on the login/getMe payload.
 *
 * The fallback below is the whole reason this is a function and not an
 * `includes` call at each site: every tenant ran for months with an EMPTY
 * `features` array while the toggles did nothing, so "not in the array" cannot
 * mean "off" until someone has actually recorded a selection
 * (`features_initialized`). Same rule, same six keys, as the server.
 */
import i18n from 'i18next';

export const DEFAULT_OFF_KEYS = [
  // Money In/Out + Partners (Finance Completeness Phase 1). Missing from this
  // list, the never-configured fallback below reads it as ON, and every tenant
  // that never recorded a feature selection would see the buttons and take a
  // 403 from routes/cash.js on the post.
  'cash_movements',
  // Chart of accounts + journal export, and the one-time opening-balance entry
  // (Finance Completeness Phase 2). Same rule as cash_movements above: a key in
  // the backend catalog but missing from this list reads as ON for every tenant
  // that never recorded a selection.
  'accounting_export',
  'opening_balances',
  // The General Ledger, 2026-09-02 — default-OFF until an accountant has seen
  // the seeded chart (spec §9). Mirrors backend/config/featureCatalog.js.
  'general_ledger',
  // Statement import + matching (Finance Completeness Phase 3). Same rule.
  'bank_reconciliation',
  'job_card_subtasks',
  'whatsapp',
  'whatsapp_cloud_api',
  // Several WhatsApp numbers (2026-10-27): default-OFF, mirrors the backend set.
  'whatsapp_multi_number',
  'jev_assist',
  // Jev photo checks (vehicles & documents) — mirrors backend/config/featureCatalog.js.
  'jev_vision',
  'jev_vision_documents',
  'smart_assistant',
  'voice_conversation',
  'voice_intake',
  'damage_scan',
  // TzamunAI check per service — mirrors backend/config/featureCatalog.js.
  'ai_service_check',
  // Fleet management (2026-10-12): default-OFF, mirrors the backend set.
  'fleet_management',
  // Acceptance evidence P1 — mirrors backend/config/featureCatalog.js.
  'customer_approval_lock',
  // Retired from the picker, but kept here for the same reason as the backend
  // catalog: the never-configured fallback reads a key missing from this list as
  // ON, which would opt un-initialized companies OUT of payment receipts.
  'legacy_downpayment_invoices',
  // Customer-delay fees (parts delivery + vehicle collection). Default-OFF,
  // ticked per company by the super-admin.
  'delay_fees',
  // Per-service warranty, 2026-09-07. Same rule as every key above: missing
  // here, the never-configured fallback reads it as ON and every such tenant
  // sees an "activate warranty" button and a monitor screen that 403s.
  'service_warranty',
  // فحص شامل / Full Check, 2026-09-08. Same MUST-be-in-both-places rule: the
  // never-configured fallback reads a key missing from this list as ON, which
  // would put a fourth job-type tile in front of every tenant that never
  // recorded a selection and 403 them on the create.
  'full_check',
  // Website builder, 2026-10-06 — mirrors backend/config/featureCatalog.js.
  'website_builder',
  // Multi-branch Release 2 (2026-10-23) — mirrors backend/config/featureCatalog.js.
  'per_branch_roles',
  'shared_customers',
  'branch_stock_transfer',
  'company_catalogue',
  'shared_suppliers',
  'branch_document_designs',
  'booking_brands',
  // Downpayment full amount incl. VAT (2026-11-03) — mirrors backend featureCatalog.
  'downpayment_full_amount',
];

export function companyHasFeature(company, key) {
  if (!company) return false;
  const features = Array.isArray(company.features) ? company.features : [];
  if (company.features_initialized) return features.includes(key);
  return features.includes(key) || !DEFAULT_OFF_KEYS.includes(key);
}

/**
 * Turn a plan refusal into an i18n key + params.
 *
 * The server sends a typed code and the raw numbers rather than a finished
 * sentence, so the message follows the user's language switch instead of
 * whatever language the request happened to be made in. Returns null for
 * anything that is not a plan limit, so callers can fall through to their
 * normal error handling.
 */
export function planLimitMessage(error) {
  const body = error?.response?.data;
  if (!body) return null;
  const data = body.data || {};

  switch (body.code) {
    case 'PLAN_USER_LIMIT':
      return { key: 'plan.userLimit', params: { limit: data.limit, current: data.current } };
    case 'PLAN_BRANCH_LIMIT':
      return { key: 'plan.branchLimit', params: { limit: data.limit, current: data.current } };
    case 'AI_QUOTA_EXCEEDED':
      return { key: 'plan.aiQuota', params: { limit: data.quota, current: data.usage } };
    case 'feature_disabled':
      // The server sends the feature's display name in both languages; pick
      // here so the sentence matches the UI the user is looking at.
      return {
        key: 'plan.featureDisabled',
        params: { feature: i18n.language === 'ar' ? data.name_ar : data.name },
      };
    default:
      return null;
  }
}
